import React, { useState } from "react";
import {
  Container,
  Box,
  Typography,
  Paper,
  Divider,
  Grid,
  Switch,
  FormControlLabel,
  Button,
  Snackbar,
  Alert,
  Card,
  CardContent,
  Stack,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import { useAuth0 } from "@auth0/auth0-react";
import { useThemeMode } from "../context/ThemeContext";

const Settings: React.FC = () => {
  const { user, isLoading } = useAuth0();
  const { mode, toggleMode } = useThemeMode();
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // UI settings
  const [darkMode, setDarkMode] = useState(mode === "dark");
  const [notificationsEnabled, setNotificationsEnabled] = useState(true);
  const [emailNotifications, setEmailNotifications] = useState(true);

  // Preferences
  const [language, setLanguage] = useState("en");
  const [timezone, setTimezone] = useState("UTC");
  const [dateFormat, setDateFormat] = useState("MM/DD/YYYY");

  const handleDarkModeToggle = () => {
    setDarkMode(!darkMode);
    toggleMode();
  };

  const handleSaveSettings = () => {
    // In a real app, you would save these to your backend
    setSuccessMessage("Settings saved successfully!");
    setTimeout(() => setSuccessMessage(null), 5000);
  };

  if (isLoading) {
    return (
      <Container maxWidth="lg">
        <Box py={4} textAlign="center">
          <Typography variant="h5">Loading settings...</Typography>
        </Box>
      </Container>
    );
  }

  if (!user) {
    return (
      <Container maxWidth="lg">
        <Box py={4} textAlign="center">
          <Typography variant="h5">
            You need to be logged in to view your settings.
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box py={4}>
        <Typography variant="h4" gutterBottom>
          Settings
        </Typography>
        <Typography variant="body1" color="text.secondary" paragraph>
          Manage your account settings and preferences
        </Typography>

        <Grid container spacing={4}>
          <Grid item xs={12} md={3}>
            <Paper elevation={1} sx={{ p: 0 }}>
              <Box sx={{ p: 2 }}>
                <Typography variant="subtitle1" fontWeight="bold">
                  Settings Menu
                </Typography>
              </Box>
              <Divider />
              <Stack>
                <Button
                  sx={{
                    justifyContent: "flex-start",
                    py: 1.5,
                    px: 2,
                    borderRadius: 0,
                    backgroundColor: (theme) =>
                      theme.palette.mode === "light"
                        ? "rgba(0, 0, 0, 0.04)"
                        : "rgba(255, 255, 255, 0.08)",
                  }}
                >
                  Account
                </Button>
                <Button
                  sx={{
                    justifyContent: "flex-start",
                    py: 1.5,
                    px: 2,
                    borderRadius: 0,
                  }}
                >
                  Appearance
                </Button>
                <Button
                  sx={{
                    justifyContent: "flex-start",
                    py: 1.5,
                    px: 2,
                    borderRadius: 0,
                  }}
                >
                  Notifications
                </Button>
                <Button
                  sx={{
                    justifyContent: "flex-start",
                    py: 1.5,
                    px: 2,
                    borderRadius: 0,
                  }}
                >
                  Privacy & Security
                </Button>
              </Stack>
            </Paper>
          </Grid>

          <Grid item xs={12} md={9}>
            <Stack spacing={3}>
              <Card elevation={1}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Account Settings
                  </Typography>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Name"
                        defaultValue={user.name}
                        fullWidth
                        disabled
                        helperText="Name is managed through your identity provider"
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label="Email"
                        defaultValue={user.email}
                        fullWidth
                        disabled
                        helperText="Email is managed through your identity provider"
                      />
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              <Card elevation={1}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Appearance
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={darkMode}
                          onChange={handleDarkModeToggle}
                        />
                      }
                      label="Dark Mode"
                    />
                    <Typography variant="body2" color="text.secondary">
                      Toggle between light and dark theme
                    </Typography>
                  </Box>
                </CardContent>
              </Card>

              <Card elevation={1}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Notifications
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={notificationsEnabled}
                          onChange={() =>
                            setNotificationsEnabled(!notificationsEnabled)
                          }
                        />
                      }
                      label="Enable Notifications"
                    />
                    <Typography variant="body2" color="text.secondary">
                      Receive in-app notifications
                    </Typography>
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={emailNotifications}
                          onChange={() =>
                            setEmailNotifications(!emailNotifications)
                          }
                          disabled={!notificationsEnabled}
                        />
                      }
                      label="Email Notifications"
                    />
                    <Typography variant="body2" color="text.secondary">
                      Receive email notifications
                    </Typography>
                  </Box>
                </CardContent>
              </Card>

              <Card elevation={1}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Preferences
                  </Typography>
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={4}>
                      <FormControl fullWidth>
                        <InputLabel id="language-select-label">
                          Language
                        </InputLabel>
                        <Select
                          labelId="language-select-label"
                          value={language}
                          label="Language"
                          onChange={(e) => setLanguage(e.target.value)}
                        >
                          <MenuItem value="en">English</MenuItem>
                          <MenuItem value="es">Spanish</MenuItem>
                          <MenuItem value="fr">French</MenuItem>
                          <MenuItem value="de">German</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <FormControl fullWidth>
                        <InputLabel id="timezone-select-label">
                          Timezone
                        </InputLabel>
                        <Select
                          labelId="timezone-select-label"
                          value={timezone}
                          label="Timezone"
                          onChange={(e) => setTimezone(e.target.value)}
                        >
                          <MenuItem value="UTC">UTC</MenuItem>
                          <MenuItem value="EST">Eastern Time (EST)</MenuItem>
                          <MenuItem value="CST">Central Time (CST)</MenuItem>
                          <MenuItem value="PST">Pacific Time (PST)</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                      <FormControl fullWidth>
                        <InputLabel id="date-format-select-label">
                          Date Format
                        </InputLabel>
                        <Select
                          labelId="date-format-select-label"
                          value={dateFormat}
                          label="Date Format"
                          onChange={(e) => setDateFormat(e.target.value)}
                        >
                          <MenuItem value="MM/DD/YYYY">MM/DD/YYYY</MenuItem>
                          <MenuItem value="DD/MM/YYYY">DD/MM/YYYY</MenuItem>
                          <MenuItem value="YYYY-MM-DD">YYYY-MM-DD</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>

              <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSaveSettings}
                >
                  Save Changes
                </Button>
              </Box>
            </Stack>
          </Grid>
        </Grid>
      </Box>

      <Snackbar
        open={Boolean(successMessage)}
        autoHideDuration={5000}
        onClose={() => setSuccessMessage(null)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert onClose={() => setSuccessMessage(null)} severity="success">
          {successMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Settings;
